/**
 * Resolve which ERPNext site this Vue app should call.
 *
 * On localhost with devProxy.enabled, calls stay on the Vite origin and the
 * dev server proxies them to the bench. Anywhere else, a client code selects
 * https://{code}.{baseDomain}, the same way the HRSency desk picks a tenant.
 */

export function isLocalDevHost(hostname) {
	const host = String(hostname || "").toLowerCase().trim();
	return host === "localhost" || host === "127.0.0.1";
}

export function isDevProxy() {
	if (typeof window === "undefined") return false;
	const config = window.__APP_CONFIG__;
	return isLocalDevHost(window.location.hostname) && !!config?.devProxy?.enabled;
}

/** Desk hosted on its own host; nginx proxies /api to the ERPNext site. */
export function isSameOriginApi() {
	if (typeof window === "undefined") return false;
	const config = window.__APP_CONFIG__;
	return !!config?.sameOriginApi || isDevProxy();
}

export function apiBaseUrlForClientCode(clientCode, config) {
	const code = String(clientCode || "").trim().toLowerCase();
	if (!code) return null;
	const domain = config?.baseDomain || "c9infotech.com";
	return `https://${code}.${domain}`;
}

/** Absolute ERPNext desk URL. Relative /app links stay inside the Vue app and 404. */
export function erpDeskUrl(path) {
	const config = typeof window !== "undefined" ? window.__APP_CONFIG__ : null;
	let code = "";
	try {
		code = (localStorage.getItem("clientCode") || "").trim().toLowerCase();
	} catch {
		code = "";
	}
	const base = apiBaseUrlForClientCode(code || "tripplan", config);
	if (!base) return path;
	const suffix = path.startsWith("/") ? path : `/${path}`;
	return base + suffix;
}

export async function initAppConfig() {
	let config = {};
	try {
		const response = await fetch("/config.json");
		if (response.ok) config = await response.json();
	} catch {
		config = {};
	}

	window.__APP_CONFIG__ = config;
	applyApiBase();
	return config;
}

/** Apply a client code and point API calls at the matching ERPNext site. */
export function applyApiBase({ clientCode } = {}) {
	if (typeof clientCode === "string") {
		const code = clientCode.trim().toLowerCase();
		if (code) localStorage.setItem("clientCode", code);
		else localStorage.removeItem("clientCode");
	}

	const config = window.__APP_CONFIG__ || {};
	const saved = (localStorage.getItem("clientCode") || "").trim().toLowerCase();
	rememberClientCode(saved);

	if (isSameOriginApi()) {
		window.__API_BASE_URL__ = window.location.origin;
		return window.__API_BASE_URL__;
	}

	if (saved) {
		window.__API_BASE_URL__ = apiBaseUrlForClientCode(saved, config);
		return window.__API_BASE_URL__;
	}

	if (config.apiBaseUrl) {
		window.__API_BASE_URL__ = String(config.apiBaseUrl).replace(/\/$/, "");
		return window.__API_BASE_URL__;
	}

	window.__API_BASE_URL__ = window.location.origin;
	return window.__API_BASE_URL__;
}

function rememberClientCode(code) {
	const value = String(code || "").trim().toLowerCase();
	if (!value) {
		document.cookie = "tp_client=; Path=/; Max-Age=0; SameSite=Lax";
		return;
	}
	document.cookie = `tp_client=${encodeURIComponent(value)}; Path=/; SameSite=Lax`;
}

export function apiBaseUrl() {
	if (typeof window !== "undefined" && window.__API_BASE_URL__) {
		return String(window.__API_BASE_URL__).replace(/\/$/, "");
	}
	return "";
}
